<template>
  <div :class="isDesktop ? 'is-desktop' : 'is-mobile'" class="filter-container">
    <div class="filter-content">
      <Collapser
        id="collapserVertrag"
        class="mb-16"
        v-model:expanded="collapserVertragExpanded"
        :iconClasses="iconClasses"
        collapserTitle="Vertrag"
        @update:expanded="() => closeCollapser('vertrag')"
      >
        <template v-slot:collapserContent>
          <Checkbox
            @change="onChangeCheckboxAllContracts"
            :model-value="checkboxAllContracts"
            :reversed="true"
            :tabindex="collapserVertragExpanded ? 0 : -1"
          >
            <template v-slot:label>
              <div data-testid="postbox-filter-checkbox-all-contracts">Alle Verträge</div>
            </template>
          </Checkbox>
          <div v-for="(postboxPolicy, index) in props.postboxPolicies" :key="index">
            <Checkbox
              @change="(_checked: boolean) => toggleCheckboxSingleContract(index)"
              :model-value="postboxPolicy.checked"
              :reversed="true"
              :tabindex="collapserVertragExpanded ? 0 : -1"
            >
              <template v-slot:label>
                <div class="label-two-rows">
                  {{
                    $t('postbox.line.' + postboxPolicy.line).length > 27
                      ? $t('postbox.line.' + postboxPolicy.line).slice(0, 27) + '...'
                      : $t('postbox.line.' + postboxPolicy.line)
                  }}
                  <br />
                  <strong>{{ postboxPolicy.kundenVSNr }}</strong>
                </div>
              </template>
            </Checkbox>
          </div>
        </template>
      </Collapser>
      <div class="divider mb-16"></div>
      <Collapser
        id="collapserZeitraum"
        class="mb-16"
        v-model:expanded="collapserZeitraumExpanded"
        :iconClasses="iconClasses"
        collapserTitle="Zeitraum"
        @update:expanded="() => closeCollapser('zeitraum')"
      >
        <template v-slot:collapserContent>
          <Radiogroup
            @input="zeitraumRadioChanged"
            :items="getZeitraumRadioItems()"
            :reversed="true"
            :value="currentZeitraumRadioCheckedValue"
          >
          </Radiogroup>
        </template>
      </Collapser>
      <div class="divider mb-16"></div>
      <Collapser
        id="collapserStatus"
        class="mb-16"
        v-model:expanded="collapserStatusExpanded"
        :iconClasses="iconClasses"
        collapserTitle="Status"
        @update:expanded="() => closeCollapser('status')"
      >
        <template v-slot:collapserContent>
          <Radiogroup
            @input="statusRadioChanged"
            :items="getStatusRadioItems()"
            :reversed="true"
            :value="currentStatusRadioCheckedValue"
          >
          </Radiogroup>
        </template>
      </Collapser>
      <div class="divider mb-8"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, type PropType, ref } from 'vue'
import {
  type PostboxPolicy,
  PostboxStatus,
  postboxStatusLabels,
  PostboxZeitraum
} from '@/types/postbox/postbox-types'
import { Checkbox, Collapser, Radiogroup } from 'de-zurich-ui-library'
import dayjs from 'dayjs'

const props = defineProps({
  documentsCount: {
    type: Number,
    required: true
  },
  postboxPolicies: {
    type: Object as PropType<Array<PostboxPolicy>>,
    required: true
  },
  postboxStatus: {
    type: String as PropType<PostboxStatus>,
    required: true
  },
  postboxZeitraum: {
    type: String as PropType<PostboxZeitraum>,
    required: true
  },
  isDesktop: {
    type: Boolean,
    required: false,
    default: false
  }
})

const emit = defineEmits<{
  'update:postboxPolicies': [Array<PostboxPolicy>]
  'update:postboxStatus': [PostboxStatus]
  'update:postboxZeitraum': [PostboxZeitraum]
}>()

const emitPostboxPolicies = (postboxPolicies: PostboxPolicy[]) => {
  emit('update:postboxPolicies', postboxPolicies)
}

const collapserVertragExpanded = ref(false)
const collapserZeitraumExpanded = ref(false)
const collapserStatusExpanded = ref(false)

const closeCollapser = (collapser: string) => {
  if (props.isDesktop) return
  switch (collapser) {
    case 'vertrag':
      collapserZeitraumExpanded.value = false
      collapserStatusExpanded.value = false
      break
    case 'zeitraum':
      collapserVertragExpanded.value = false
      collapserStatusExpanded.value = false
      break
    case 'status':
      collapserVertragExpanded.value = false
      collapserZeitraumExpanded.value = false
      break
  }
}

const iconClasses = {
  expanded: 'icon-chevron-up',
  collapsed: 'icon-chevron-down'
}

const zeitraumRadioChanged = (val: string): void => {
  emit('update:postboxZeitraum', val.split('__')[0] as PostboxZeitraum)
}

const getZeitraumRadioItems = (): { id: string; label: string }[] => {
  const lastTwoYears = [dayjs().year(), dayjs().year() - 1].map(String)
  const allItem = {
    id: PostboxZeitraum.ALL + '__status',
    label: PostboxZeitraum.ALL
  }
  const relativeItems = [
    PostboxZeitraum.LAST_30_D,
    PostboxZeitraum.LAST_90_D,
    PostboxZeitraum.LAST_6_M
  ].map((value: string) => ({
    id: value + '__status',
    label: String(value) // explizit als string
  }))
  const yearItems = lastTwoYears.map((year) => ({
    id: year + '__status',
    label: year
  }))

  return [allItem, ...relativeItems, ...yearItems]
}

const currentZeitraumRadioCheckedValue = computed(() => {
  return getZeitraumRadioItems().find(
    (radioItem) => radioItem.id.split('__')[0] === props.postboxZeitraum
  )!.id
})

const defaultZeitraumRadioCheckedValue = PostboxZeitraum.ALL + '__status'

const statusRadioChanged = (val: string): void => {
  emit('update:postboxStatus', val.split('__')[0] as PostboxStatus)
}

const getStatusRadioItems = (): { id: string; label: string }[] => {
  return [PostboxStatus.ALL, PostboxStatus.UNREAD, PostboxStatus.READ].map((value) => ({
    id: value + '__status',
    label: postboxStatusLabels[value as PostboxStatus] // Use the mapping for the label
  }))
}

const currentStatusRadioCheckedValue = computed(() => {
  return getStatusRadioItems().find(
    (radioItem) => radioItem.id.split('__')[0] === props.postboxStatus
  )!.id
})

const defaultStatusRadioCheckedValue = PostboxStatus.ALL + '__status'

const toggleCheckboxSingleContract = (index: number): void => {
  const postboxPolicies = props.postboxPolicies
  postboxPolicies[index]!.checked = !postboxPolicies[index]!.checked
  emitPostboxPolicies(postboxPolicies)
}

const checkboxAllContracts = computed(() => {
  return props.postboxPolicies.every((postboxPolicy) => postboxPolicy.checked)
})

const onChangeCheckboxAllContracts = (checked: boolean): void => {
  props.postboxPolicies.forEach((postboxPolicy) => {
    postboxPolicy.checked = checked
  })
}

const resetFilter = () => {
  collapserVertragExpanded.value = false
  collapserZeitraumExpanded.value = false
  collapserStatusExpanded.value = false
  props.postboxPolicies.forEach((postboxPolicy) => {
    postboxPolicy.checked = true
  })
  emitPostboxPolicies(props.postboxPolicies)
  emit(
    'update:postboxStatus',
    defaultStatusRadioCheckedValue.split('__')[0] as PostboxStatus
  )
  emit(
    'update:postboxZeitraum',
    defaultZeitraumRadioCheckedValue.split('__')[0] as PostboxZeitraum
  )
}

defineExpose({
  resetFilter
})
</script>

<style lang="scss" scoped>
.label-two-rows {
  line-height: 19px;
  margin-top: -6px;
}

:deep() .collapser__headline--xs .icon span,
:deep() .collapser__headline--sm .icon span {
  margin-right: -3px !important;
}

.is-styled .collapser__block div:not(:last-of-type) {
  margin-bottom: 12px;
}

:deep() .button--cta2--small {
  border: 2px solid var(--cta2-border-color) !important;
}

:deep(.is-styled .collapser__headline .title--xs),
:deep(.is-styled .collapser__headline .title--sm) {
  font-size: 16px;
}

:deep(.is-styled .collapser__headline--xs .icon span[class*='icon']),
:deep(.is-styled .collapser__headline--sm .icon span[class*='icon']) {
  margin-right: 0;
}

:deep() .checkbox .checkbox__label::after,
:deep() .checkbox .checkbox__control:checked + .checkbox__label::before {
  transform: scale(0.85) !important;
}

:deep(.checkbox__label),
:deep(.radio__label) {
  font-size: 14px;
}

.divider {
  margin-left: -16px;
  margin-right: -16px;
  border-bottom: 1px solid var(--postbox-filter-background);
}

.filter-container {
  max-width: 600px;
  padding: 0 16px;
  background-color: var(--postbox-filter-background);
}

.filter-container.is-desktop {
  padding: 0;
}

.filter-content {
  border-radius: 10px 10px 0 0;
  padding: 16px 16px 1px 16px;
  background-color: white;
}
</style>
